import { AlertTriangle, FileText, ClipboardList, PieChart } from 'lucide-react'
import { Modal, ModalFooter } from '../../../components/ui/Modal'
import type { DealHubSession } from '../types'

interface DuplicateSessionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  onUseExisting: (session: DealHubSession) => void
  sessions: DealHubSession[]
  dealName: string
}

export function DuplicateSessionModal({
  isOpen,
  onClose,
  onConfirm,
  onUseExisting,
  sessions,
  dealName,
}: DuplicateSessionModalProps) {
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Existing Sessions Found" size="lg">
      <div className="space-y-4">
        {/* Warning message */}
        <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/30 rounded-lg">
          <AlertTriangle className="text-warning flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-sm text-[var(--text-primary)]">
              <strong>{dealName}</strong> already has {sessions.length} existing session{sessions.length > 1 ? 's' : ''}.
            </p>
            <p className="text-sm text-[var(--text-secondary)] mt-1">
              Consider using an existing session instead of creating a new one to avoid duplicate work.
            </p>
          </div>
        </div>

        {/* Existing sessions list */}
        <div>
          <h4 className="text-sm font-medium text-[var(--text-primary)] mb-2">Existing Sessions:</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {sessions.map((session) => (
              <button
                key={session.id}
                onClick={() => onUseExisting(session)}
                className="w-full p-3 bg-[var(--bg-tertiary)] hover:bg-primary/10 border border-[var(--border-color)] hover:border-primary/30 rounded-lg transition-colors text-left"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm text-[var(--text-primary)]">#{session.id}</span>
                      <span className="text-xs text-[var(--text-muted)]">
                        Created {formatDate(session.createdAt)}
                      </span>
                    </div>
                    <div className="text-xs text-[var(--text-secondary)] mt-1">
                      by {session.createdBy?.name || 'Unknown'}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div
                      className={`p-1 rounded ${
                        session.hasQuote ? 'bg-primary/20 text-primary' : 'bg-[var(--bg-secondary)] text-gray-500'
                      }`}
                      title={session.hasQuote ? 'Has Quote' : 'No Quote'}
                    >
                      <FileText size={14} />
                    </div>
                    <div
                      className={`p-1 rounded ${
                        session.hasScope ? 'bg-primary/20 text-primary' : 'bg-[var(--bg-secondary)] text-gray-500'
                      }`}
                      title={session.hasScope ? 'Has Scope' : 'No Scope'}
                    >
                      <ClipboardList size={14} />
                    </div>
                    <div
                      className={`p-1 rounded ${
                        session.hasROI ? 'bg-primary/20 text-primary' : 'bg-[var(--bg-secondary)] text-gray-500'
                      }`}
                      title={session.hasROI ? 'Has ROI' : 'No ROI'}
                    >
                      <PieChart size={14} />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      <ModalFooter>
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
        >
          Create New Anyway
        </button>
      </ModalFooter>
    </Modal>
  )
}
